/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './Login';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';

import { toAbsoluteUrl } from '../../../components/functions';
import { useSelector } from 'react-redux';

export function AuthPage() {
    const permissions = useSelector((state) => state.auth.permissions);
    if (permissions) return <Navigate to="/home" />;

    // Determine portal type
    const hostname = window.location.hostname;
    // Member portal: must include 'member.' subdomain
    const isMemberPortal = hostname.includes('member.');
    const isReadingPlexAdmin = hostname.includes('readingplex') && hostname.includes('admin.');
    // MathPlex admin: must NOT be a member portal
    const isMathPlexAdmin = !isMemberPortal && (
        (hostname.includes('mathplextutoring.com') && hostname.includes('admin.')) ||
        (hostname.includes('mathplextutoring.com') && !hostname.includes('readingplex') && !hostname.includes('mp.'))
    );

    return (
        <div className="relative min-h-screen">
            {/* Background */}
            <div className="h-screen fixed w-full">
                <img
                    alt="background"
                    className="h-full w-full object-cover"
                    src={toAbsoluteUrl("/media/bg/bg-2.jpg")}
                />
            </div>

            {/* Left Section - Branding (Hidden on mobile, shown on md+) */}
            <div className="hidden md:block w-1/2 h-screen fixed left-0 top-0 relative">
                {isMemberPortal ? (
                    // MEMBER PORTAL: Show both logos side by side
                    <>
                        {/* Both Logos Container */}
                        <div className="absolute z-10 top-8 md:top-12 left-4 md:left-8 flex items-center gap-2 md:gap-4">
                            <img
                                alt="MathPlex Logo"
                                className="max-h-16 md:max-h-20 object-contain"
                                src={toAbsoluteUrl("/media/logos/darklong.png")}
                                onError={(e) => {
                                    console.error('MathPlex logo failed to load');
                                    e.target.style.display = 'none';
                                }}
                            />
                            <span className="text-white text-xl md:text-2xl font-bold mx-1 md:mx-2">+</span>
                            <img
                                alt="ReadingPlex Logo"
                                className="max-h-16 md:max-h-20 object-contain"
                                src={toAbsoluteUrl("/media/logos/readingplex-logo.png")}
                                onError={(e) => {
                                    console.error('ReadingPlex logo failed to load');
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>

                        {/* Welcome Message */}
                        <div className="absolute z-10 top-64 md:top-80 pt-4 left-6 md:left-12">
                            <div className="text-2xl md:text-3xl text-white font-semibold">
                                Welcome to the
                            </div>
                        </div>
                        <div className="absolute z-10 top-80 md:top-96 left-6 md:left-12">
                            <div className="text-2xl md:text-3xl text-white font-semibold">
                                MathPlex & ReadingPlex Member Portal!
                            </div>
                        </div>
                    </>
                ) : (
                    // ADMIN PORTAL: Show single logo based on domain
                    <>
                        <img
                            alt={isReadingPlexAdmin ? "ReadingPlex Logo" : "MathPlex Logo"}
                            className="max-h-20 md:max-h-24 absolute z-10 top-8 md:top-12 left-4 md:left-8 object-contain"
                            src={toAbsoluteUrl(
                                isReadingPlexAdmin
                                    ? "/media/logos/readingplex-logo.png"
                                    : "/media/logos/darklong.png"
                            )}
                        />
                        <div className="absolute z-10 top-64 md:top-80 pt-4 left-6 md:left-12">
                            <div className="text-2xl md:text-3xl text-white font-semibold">
                                Welcome to the
                            </div>
                        </div>
                        <div className="absolute z-10 top-80 md:top-96 left-6 md:left-12">
                            <div className="text-2xl md:text-3xl text-white font-semibold">
                                {isReadingPlexAdmin ? 'ReadingPlex Admin Portal!' : 'MathPlex Admin Portal!'}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Mobile: Show logos at top (only on mobile) */}
            <div className="md:hidden absolute top-4 left-4 z-20 flex items-center gap-2">
                {isMemberPortal ? (
                    <>
                        <img
                            alt="MathPlex Logo"
                            className="max-h-12 object-contain"
                            src={toAbsoluteUrl("/media/logos/darklong.png")}
                            onError={(e) => {
                                console.error('MathPlex logo failed to load');
                                e.target.style.display = 'none';
                            }}
                        />
                        <span className="text-white text-lg font-bold">+</span>
                        <img
                            alt="ReadingPlex Logo"
                            className="max-h-12 object-contain"
                            src={toAbsoluteUrl("/media/logos/readingplex-logo.png")}
                            onError={(e) => {
                                console.error('ReadingPlex logo failed to load');
                                e.target.style.display = 'none';
                            }}
                        />
                    </>
                ) : (
                    <img
                        alt={isReadingPlexAdmin ? "ReadingPlex Logo" : "MathPlex Logo"}
                        className="max-h-12 object-contain"
                        src={toAbsoluteUrl(
                            isReadingPlexAdmin
                                ? "/media/logos/readingplex-logo.png"
                                : "/media/logos/darklong.png"
                        )}
                    />
                )}
            </div>

            {/* Right Section - Login Form (Full width on mobile, half on desktop) */}
            <div className="relative md:fixed md:left-1/2 md:top-0 w-full md:w-1/2 min-h-screen md:h-screen z-10 bg-white overflow-y-auto">
                <div className="min-h-full flex items-center justify-center py-8 px-4 md:px-0">
                    <div className="text-center w-full max-w-md">
                        <Routes>
                            <Route path="login" element={<Login />} />
                            <Route path="forgot-password" element={<ForgotPassword />} />
                            <Route path="reset-password/*" element={<ResetPassword />} />
                            <Route path="verify/*" element={<ResetPassword />} />
                            <Route path="*" element={<Navigate to="/login" />} />
                        </Routes>
                    </div>
                </div>
            </div>
        </div>
    );
}
